import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { allSections } from '@/utils/content';

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const navLinks = [
    { to: '/', label: '首页' },
    ...allSections.map((section) => ({ to: `/section/${section.slug}`, label: section.shortName })),
    { to: '/about', label: '关于我们' },
  ];

  const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) {
      return;
    }
    navigate(`/?q=${encodeURIComponent(value)}`);
    setKeyword('');
    setMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="h-1 w-full bg-primary-500" />
      <div className="mx-auto flex h-16 w-full items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-background-50">
            <i className="ri-water-flash-fill text-lg" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-xl font-black tracking-tight text-foreground-950">
              17吃瓜网
            </span>
            <span className="hidden text-[11px] tracking-[0.2em] text-foreground-500 sm:block">
              八卦 · 话题 · 分区
            </span>
          </span>
        </Link>

        <nav className="hidden flex-1 items-center gap-1 lg:flex" aria-label="站点主导航">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === '/'}
              className={({ isActive }) =>
                `cursor-pointer rounded-full px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100 hover:text-primary-600'
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <form
          onSubmit={handleSearch}
          className="ml-auto hidden items-center gap-2 rounded-full border border-background-200 bg-background-100 px-3 py-1.5 md:flex"
          role="search"
        >
          <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-base" />
          </span>
          <input
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜吃瓜话题…"
            aria-label="站内搜索"
            className="w-28 bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400 lg:w-40"
          />
          <button
            type="submit"
            className="cursor-pointer whitespace-nowrap rounded-full bg-secondary-500 px-3 py-1 text-xs font-medium text-background-50 transition-colors hover:bg-secondary-600"
          >
            搜索
          </button>
        </form>

        <button
          type="button"
          aria-label="打开导航菜单"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((prev) => !prev)}
          className="ml-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-800 lg:hidden"
        >
          <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 lg:hidden">
          <nav className="mx-auto flex w-full flex-col px-4 py-3" aria-label="移动端导航">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === '/'}
                className={({ isActive }) =>
                  `cursor-pointer rounded-md px-3 py-2.5 text-sm font-medium ${
                    isActive ? 'bg-primary-100 text-primary-700' : 'text-foreground-700'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <form onSubmit={handleSearch} className="mt-3 flex items-center gap-2 rounded-lg border border-background-200 bg-background-100 px-3 py-2">
              <i className="ri-search-line text-foreground-400" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜吃瓜话题…"
                aria-label="站内搜索"
                className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
              />
              <button type="submit" className="cursor-pointer whitespace-nowrap text-sm font-medium text-primary-600">
                搜索
              </button>
            </form>
          </nav>
        </div>
      )}
    </header>
  );
}